'use client'

import { motion } from 'framer-motion'
import { Star, Quote, Building, User, ThumbsUp, Award } from 'lucide-react'
import Link from 'next/link'
import { useCompanyInfo } from '@/contexts/CompanyInfoContext'
import DynamicCompanyInfo from '@/components/DynamicCompanyInfo'

export default function TestimonialsPage() {
  const { companyInfo } = useCompanyInfo();
  const testimonials = [
    {
      name: 'Robert Johnson',
      position: 'CTO',
      company: 'TechFlow Solutions',
      industry: 'FinTech',
      rating: 5,
      image: '👨‍💼',
      testimonial: `${companyInfo?.companyName || 'Our team'} transformed our legacy system into a modern, scalable platform. Their team's expertise in cloud migration and security implementation exceeded our expectations. The project was delivered on time and within budget.`,
      projectType: 'Cloud Migration & Modernization',
      duration: '6 months',
      results: ['40% cost reduction', '99.9% uptime achieved', '3x faster performance']
    },
    {
      name: 'Sarah Mitchell',
      position: 'VP of Operations',
      company: 'Global Retail Corp',
      industry: 'E-commerce',
      rating: 5,
      image: '👩‍💼',
      testimonial: `The e-commerce platform developed by ${companyInfo?.companyName || 'our team'} has revolutionized our online presence. Sales increased by 150% in the first quarter post-launch. Their attention to user experience and mobile optimization was exceptional.`,
      projectType: 'E-commerce Platform Development',
      duration: '4 months',
      results: ['150% sales increase', '300% mobile traffic growth', '45% better conversion rate']
    },
    {
      name: 'David Park',
      position: 'Founder & CEO',
      company: 'HealthTech Innovations',
      industry: 'Healthcare',
      rating: 5,
      image: '👨‍⚕️',
      testimonial: `Working with ${companyInfo?.companyName || 'this company'} on our healthcare management system was a game-changer. They understood the compliance requirements and delivered a HIPAA-compliant solution that streamlined our operations significantly.`,
      projectType: 'Healthcare Management System',
      duration: '8 months',
      results: ['HIPAA compliance achieved', '60% workflow efficiency', '90% staff satisfaction']
    },
    {
      name: 'Maria Rodriguez',
      position: 'Head of Digital',
      company: 'Banking Plus',
      industry: 'Banking & Finance',
      rating: 5,
      image: '👩‍💻',
      testimonial: `The mobile banking app created by ${companyInfo?.companyName || 'this company'} has received outstanding user reviews. Their agile approach and regular communication made the development process smooth and transparent.`,
      projectType: 'Mobile Banking Application',
      duration: '5 months',
      results: ['4.8 star app rating', '1M+ downloads', '80% user retention']
    },
    {
      name: 'James Wilson',
      position: 'Operations Director',
      company: 'Manufacturing Pro',
      industry: 'Manufacturing',
      rating: 5,
      image: '👨‍🏭',
      testimonial: `The IoT solution implemented by ${companyInfo?.companyName || 'this company'} for our smart factory initiative has improved our production efficiency by 35%. Their expertise in industrial automation is truly impressive.`,
      projectType: 'Smart Factory IoT Implementation',
      duration: '10 months',
      results: ['35% efficiency improvement', '25% cost reduction', '99% equipment uptime']
    },
    {
      name: 'Lisa Chen',
      position: 'Marketing Director',
      company: 'EduTech Global',
      industry: 'Education Technology',
      rating: 5,
      image: '👩‍🎓',
      testimonial: `Our online learning platform powered by AI recommendations has transformed education delivery. ${companyInfo?.companyName || 'This company'}'s team delivered an innovative solution that adapts to each student's learning pace.`,
      projectType: 'AI-Powered Learning Platform',
      duration: '7 months',
      results: ['200% student engagement', '50% faster learning', '95% course completion']
    }
  ]

  const stats = [
    { value: '150+', label: 'Happy Clients' },
    { value: '4.9★', label: 'Average Rating' },
    { value: '98%', label: 'Client Retention' },
    { value: '400+', label: 'Projects Completed' }
  ]

  const industries = [
    'FinTech & Banking',
    'E-commerce & Retail', 
    'Healthcare & Medical',
    'Education Technology',
    'Manufacturing & IoT',
    'Real Estate & PropTech',
    'Logistics & Transportation',
    'Entertainment & Media'
  ]

  return (
    <main className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Client <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Success Stories</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Discover how we've helped businesses across industries achieve their digital transformation goals and drive measurable results.
            </p>
          </motion.div>

          {/* Stats */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 max-w-4xl mx-auto"
          >
            {stats.map((stat, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="text-3xl font-bold text-blue-600 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">What Our Clients Say</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              <DynamicCompanyInfo type="companyName">
                {(companyInfo) => (
                  `Real feedback from clients who have experienced the ${companyInfo?.companyName || 'company'} difference.`
                )}
              </DynamicCompanyInfo>
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                {/* Quote Icon */}
                <div className="flex justify-between items-start mb-6">
                  <Quote className="w-8 h-8 text-blue-600" />
                  <div className="flex space-x-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-500 fill-current" />
                    ))}
                  </div>
                </div>

                {/* Testimonial Text */}
                <p className="text-gray-700 leading-relaxed mb-6 text-lg">"{testimonial.testimonial}"</p>

                {/* Project Details */}
                <div className="bg-white rounded-xl p-4 mb-6">
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Project:</span>
                      <p className="font-semibold text-gray-900">{testimonial.projectType}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Duration:</span>
                      <p className="font-semibold text-gray-900">{testimonial.duration}</p>
                    </div>
                  </div>
                </div>

                {/* Results */}
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <Award className="w-4 h-4 text-green-500 mr-2" />
                    Key Results:
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {testimonial.results.map((result, resultIndex) => (
                      <span key={resultIndex} className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                        {result}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Client Info */}
                <div className="flex items-center pt-4 border-t border-gray-200">
                  <div className="text-3xl mr-4">{testimonial.image}</div>
                  <div>
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    <p className="text-blue-600 font-medium">{testimonial.position}</p>
                    <p className="text-gray-600 text-sm">{testimonial.company} • {testimonial.industry}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Industries We Serve</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our expertise spans across multiple industries, delivering tailored solutions for diverse business needs.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 text-center shadow-md hover:shadow-lg transition-all duration-300"
              >
                <Building className="w-8 h-8 text-blue-600 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900">{industry}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-purple-600">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Join Our Success Stories?</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Let's discuss how we can help you achieve similar results and transform your business with innovative technology solutions.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link href="/contact" className="bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:shadow-xl transition-all duration-300">
                Start Your Project
              </Link>
              <Link href="/services" className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300">
                View Our Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  )
}